/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useEffect, useRef, useState } from "react";
import _ from "lodash";
import { useTranslation } from "@i18n";
import folderImg from "../../assets/icons/svg/folder.svg";
import folderLockImg from "../../assets/icons/svg/folderlock.svg";
import spaceImg from "../../assets/icons/svg/spaces.svg";
import type { FileManagerData, FileManagerRow } from "./fileSystemServer.schema";
import type { GridClickEvent } from "@CubeComponents/html/SlickGrid1";

/**
 * The slice of the tree grid's imperative handle this hook drives. Stated structurally rather than
 * as `SlickGrid1Ref<T>` so the caller's row type stays its own business.
 */
interface TreeGridHandle {
  slickGrid: {
    getDataItem: (row: number) => FileManagerRow;
    setSelectedRows: (rows: number[]) => void;
    getCellNode: (row: number, cell: number) => HTMLElement | null;
  };
  dataView?: { getRowById: (id: string) => number | undefined };
  treeDataService?: { toggleTreeDataCollapse: (collapse: boolean) => void };
}
type TreeGridRef = { current: TreeGridHandle | null };

/** Which folder ids are collapsed, keyed by row id. */
type CollapsedState = Record<string, boolean>;

/** Method shorthand on the callbacks — see the note in `useFileManagerShared`. */
interface UseFileManagerLeftSideTreeProps {
  setSelectedFolder(folder: FileManagerRow): void;
  selectedFolder: FileManagerRow;
  fileManagerData: FileManagerData;
  onFileLoad(files: FileManagerRow[]): void;
}

function useFileManagerLeftSideTree({
  setSelectedFolder,
  selectedFolder,
  fileManagerData,
  onFileLoad,
}: UseFileManagerLeftSideTreeProps) {
  const { t } = useTranslation();

  // State
  const [folderTreeData, setFolderTreeData] = useState<FileManagerRow[]>([]);
  const [folderCollapsedState, setFolderCollapsedState] = useState<CollapsedState>({});
  const [isFileClicked, setIsFileClicked] = useState<boolean>(false);

  // Refs
  const tempRef = useRef<TreeGridHandle>(null);

  // Helper functions
  // `rootParentId` seeds the recursion: "0" for a single space root, `null` for the project tree where
  // each Space is a top-level node (parentId === null). `startLevel` is the tree depth of that first
  // layer (0 for project Space roots; 1 for single-space folders that sit under a separate depth-0 root).
  function sortWithHierarchy(arr: FileManagerRow[], rootParentId: string | null = "0", startLevel = 1) {
    // `parentId` is asserted to `string` at each index: it is `string | null` and a null parent
    // deliberately buckets under the "null" key, exactly as at HEAD.
    const byParent = arr.reduce((acc: Record<string, FileManagerRow[]>, item: FileManagerRow) => {
      if (!acc[item.parentId as string]) {
        acc[item.parentId as string] = [];
      }
      acc[item.parentId as string]!.push(item);
      return acc;
    }, {});

    function sortRecursive(parentId: string | null, level: number): FileManagerRow[] {
      const children = byParent[parentId as string] || [];
      // Default Space pins to the top of its level; everything else is alphabetical. (Only Space roots
      // carry isDefault, so folders fall straight through to the name sort.)
      children.sort((a: FileManagerRow, b: FileManagerRow) => {
        const defaultDiff = (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0);
        // KNOWN BUG, preserved verbatim: a row with no `name` throws here rather than sorting last.
        return defaultDiff || a.name!.localeCompare(b.name as string);
      });
      // Stamp the real tree depth so the SlickGrid tree (levelPropName: "treeLevel") indents each row
      // correctly, rather than relying on the `path` field that isn't populated on the raw data yet.
      return children.flatMap((child: FileManagerRow) => [
        { ...child, __depth: level },
        ...sortRecursive(child.id as string, level + 1),
      ]);
    }

    return sortRecursive(rootParentId, startLevel);
  }

  // Handlers
  const handleOnClick = (args: GridClickEvent, treeViewRef: TreeGridRef) => {
    setIsFileClicked(true);
    const rowIndex = args.detail.args.row;
    const clickedRow = treeViewRef?.current?.slickGrid?.getDataItem(rowIndex) || {};
    const target = args.detail.eventData.target;

    const toogleClicked =
      target.classList.contains("slick-group-toggle") ||
      target.classList.contains("slick-cell-checkboxsel") ||
      target.closest(".slick-cell-checkboxsel");
    setFolderCollapsedState((prev: CollapsedState) => {
      prev[clickedRow.id as string] = toogleClicked ? !prev[clickedRow.id as string] : false;
      return prev;
    });
    setSelectedFolder(clickedRow);
  };

  const titleFormatter = (data: number, value: number, a: unknown, b: unknown, args: FileManagerRow) => {
    let iconSrc = folderImg;
    if (!args?.permission?.read) {
      iconSrc = folderLockImg;
    }
    if (args.id == "0" || args.isSpaceRoot) {
      iconSrc = spaceImg;
    }
    const defaultChip = args.isDefault
      ? `<span style="margin-inline-start:0.375rem;font-size:0.625rem;line-height:1;padding:0.375rem 0.75rem;border-radius:0.625rem;background:#666a84;color:#ffffff;font-weight:600;white-space:nowrap;">${t("Default")}</span>`
      : "";
    // Escape the (user-controlled) name — this string is rendered as raw HTML by the SlickGrid formatter,
    // so an unescaped name like `<img src=x onerror=...>` would execute.
    return `<span style="display:flex;align-items:center;gap:0.375rem; ">
      <img src="${iconSrc}" alt="icon" style="width:1.25rem;height:1.25rem;" />
      ${_.escape(args.name ?? "")}
      ${defaultChip}
    </span>
  `;
  };

  const collapseParentNodesWithoutChildData = (treeViewRef: TreeGridRef) => {
    setTimeout(() => {
      treeViewRef.current?.treeDataService!.toggleTreeDataCollapse(false);
    }, 0);
  };

  const syncSelectedFolder = (treeViewRef: TreeGridRef) => {
    if (treeViewRef.current?.dataView) {
      const dataView = treeViewRef.current?.dataView;
      // KNOWN BUG, preserved verbatim: `getRowById` returns `undefined` for a folder that is not in
      // the tree, and that `undefined` is passed straight into `setSelectedRows`.
      const rowIndex = dataView.getRowById(selectedFolder.id as string);
      treeViewRef.current.slickGrid.setSelectedRows([rowIndex!]);

      if (selectedFolder.isBulkFetch) {
        const index = folderTreeData.findIndex((d: FileManagerRow) => d.id == selectedFolder.id);
        const node = treeViewRef.current.slickGrid.getCellNode(index, 0);
        const rowNode = node?.closest(".slick-row");
        if (rowNode) {
          rowNode.scrollIntoView({ behavior: "auto", block: "center" });
        }
      }
    }
  };

  // Effects
  useEffect(() => {
    const allItems =
      _.cloneDeep(fileManagerData.files)?.filter(
        (item: FileManagerRow) =>
          // @ts-expect-error -- the row's `id` is optional on `FileManagerRow` (it is `"0"` for the root sentinel and absent on a synthesised node) but the tree helper requires `string`.
          ["RESOURCE_TYPE_TRANSMITTAL", "RESOURCE_TYPE_FOLDER"].includes(item.resourceType) && item.id != "0",
      ) || [];

    onFileLoad(allItems);

    // Project ("Folders") mode is detected structurally: the data carries one synthetic Space-root node
    // per Space (parentId === null). There is then no single synthetic root — each Space IS a root.
    const isProjectTree = allItems.some((item: FileManagerRow) => item.isSpaceRoot);

    // `__depth` is the real tree depth stamped by sortWithHierarchy (root = 0, its folders = 1, …).
    const decorate = (d: FileManagerRow) => {
      const level = (d.__depth as number | undefined) ?? 0;
      return {
        ...d,
        indentLevel: level,
        hasChild: !!d.hasChild,
        __hasChildren: !!d.hasChild,
        treeLevel: level,
        __collapsed: folderCollapsedState[d.id as string],
        isExpanded: !folderCollapsedState[d.id as string],
      };
    };

    if (isProjectTree) {
      // Space roots sit at depth 0; their folders/sub-folders nest beneath (1, 2, …).
      const updatedData: FileManagerRow[] = sortWithHierarchy(allItems, null, 0).map(decorate);
      setFolderTreeData(updatedData);
      return;
    }

    const root: FileManagerRow = {
      ...fileManagerData.cwd,
      nodeId: fileManagerData.cwd?.id || "cwd",
      id: "0",
      indentLevel: 0,
      parentId: null,
      treeLevel: 0,
      hasChild: true,
      __hasChildren: true,
      __collapsed: false,
      isExpanded: true,
    };

    // The single synthetic root is depth 0, so its folders start at depth 1.
    const updatedData: FileManagerRow[] = sortWithHierarchy(allItems, "0", 1).map(decorate);

    setFolderTreeData([root, ...updatedData]);
  }, [folderCollapsedState]);

  useEffect(() => {
    const allItems =
      fileManagerData.files?.filter(
        (item: FileManagerRow) =>
          // @ts-expect-error -- same as line 153: optional `FileManagerRow.id` into a required `string`.
          ["RESOURCE_TYPE_TRANSMITTAL", "RESOURCE_TYPE_FOLDER"].includes(item.resourceType) && item.id != "0",
      ) || [];

    setFolderCollapsedState((prev) => {
      const newObj = { ...prev };
      allItems.map((f: FileManagerRow) => {
        if (typeof newObj[f.id as string] == "undefined" || !isFileClicked) {
          const childItem = allItems.find((item) => item.parentId == f.id);
          newObj[f.id as string] = !childItem;
        }
      });
      return newObj;
    });
    setIsFileClicked(false);
  }, [fileManagerData.files]);

  // Deep-link refresh restore: when a nested folder is restored (updateCurrentDir stamps isBulkFetch), expand
  // its Space root + ancestor chain + the folder itself so it's revealed and highlighted in the tree. Gated
  // on isBulkFetch, so normal clicks and single-space mode are untouched (they never set it).
  useEffect(() => {
    if (!selectedFolder?.isBulkFetch) return;
    const expandIds = [
      String(selectedFolder.spaceId ?? ""),
      ...(selectedFolder.parentIdArray || []).map((id: string) => String(id)),
      String(selectedFolder.id ?? ""),
    ].filter((id: string) => id && id !== "0");
    if (!expandIds.length) return;
    setFolderCollapsedState((prev: CollapsedState) => {
      const next = { ...prev };
      expandIds.forEach((id: string) => {
        next[id] = false;
      });
      return next;
    });
  }, [selectedFolder?.id]);

  return {
    // State
    folderTreeData,
    setFolderTreeData,
    folderCollapsedState,
    setFolderCollapsedState,

    // Refs
    tempRef,

    // Handlers
    sortWithHierarchy,
    handleOnClick,
    titleFormatter,
    collapseParentNodesWithoutChildData,
    syncSelectedFolder,

    // Props passthrough
    selectedFolder,
  };
}

export default useFileManagerLeftSideTree;
